import React, { useState, useEffect } from 'react';
import { getServices, getAvailableBlocks, createAppointment, getBranchesPublic, getDeliveryFee } from '../lib/api';
import { formatCurrency, formatTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Car, Calendar as CalendarIcon, Clock, User, Phone, CheckCircle, 
  Loader2, ArrowRight, ArrowLeft, MapPin, Truck, Home, Store, Plus, Minus, Info, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';

const VEHICLE_TYPES = [
  { value: 'sedan', label: 'Sedán' },
  { value: 'suv', label: 'SUV' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'motocicleta', label: 'Motocicleta' },
  { value: 'otro', label: 'Otro' },
];

const SERVICE_TYPES = [
  { value: 'centro_lavado', label: 'Centro de Lavado', desc: 'Trae tu vehículo a nuestras instalaciones', icon: Store },
  { value: 'domicilio', label: 'Servicio a Domicilio', desc: 'Vamos a donde estés', icon: Home },
  { value: 'recogida_entrega', label: 'Recogida y Entrega', desc: 'Recogemos y entregamos tu vehículo', icon: Truck },
];

const TIME_BLOCK_LABELS = {
  manana: { label: 'Mañana', time: 'A partir de las 8:30', note: 'Dependiente del horario de acceso de tu Fraccionamiento', icon: '🌅' },
  tarde: { label: 'Tarde', time: '1:00 - 4:00 PM', note: 'Último servicio a agendar 3:30 o 4:00', icon: '🌇' },
};

export default function PublicBookingPage() {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(20);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serviceCategory, setServiceCategory] = useState('');
  const [availableBlocks, setAvailableBlocks] = useState({});
  const [loadingBlocks, setLoadingBlocks] = useState(false);

  const [formData, setFormData] = useState({
    service_type: '',
    name: '', phone: '', vehicle_type: 'sedan',
    service_id: '', date: null, time_block: '',
    notes: '', address: '', google_maps_link: '',
    units: 1, branch_id: ''
  });
  const [additionalUnits, setAdditionalUnits] = useState([]);
  // additionalUnits: [{ service_id: '', vehicle_type: 'sedan' }, ...]

  useEffect(() => { fetchServices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchServices = async () => {
    try {
      const [servicesRes, branchesRes, feeRes] = await Promise.all([
        getServices(),
        getBranchesPublic().catch(() => ({ data: [] })),
        getDeliveryFee().catch(() => ({ data: { amount: 20 } })),
      ]);
      setServices(servicesRes.data);
      setBranches(branchesRes.data);
      setDeliveryFee(feeRes.data?.amount ?? 20);
    } catch (error) { /* silent */ }
    setLoading(false);
  };

  const fetchBlocks = async (date) => {
    setLoadingBlocks(true);
    try {
      const res = await getAvailableBlocks(format(date, 'yyyy-MM-dd'));
      setAvailableBlocks(res.data);
    } catch (error) { setAvailableBlocks({}); }
    setLoadingBlocks(false);
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date, time_block: '' });
    fetchBlocks(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.service_type !== 'centro_lavado' && !formData.address && !formData.google_maps_link) {
      toast.error('Se requiere dirección o link de Google Maps');
      return;
    }
    setSubmitting(true);
    try {
      await createAppointment({
        name: formData.name, phone: formData.phone,
        vehicle_type: formData.vehicle_type,
        service_id: formData.service_id,
        date: format(formData.date, 'yyyy-MM-dd'),
        time_block: formData.time_block || null,
        service_type: formData.service_type,
        branch_id: formData.branch_id || null,
        address: formData.address || null,
        google_maps_link: formData.google_maps_link || null,
        notes: formData.notes || null,
        units: formData.units,
        additional_units: additionalUnits.length > 0 ? additionalUnits : null
      });
      setSuccess(true);
      toast.success('Solicitud enviada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agendar');
    }
    setSubmitting(false);
  };

  const totalSteps = 5;
  const needsAddress = formData.service_type === 'domicilio' || formData.service_type === 'recogida_entrega';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Solicitud Enviada</h2>
          <p className="text-slate-600 mb-6">Tu cita será confirmada por nuestro equipo. Te contactaremos al número proporcionado.</p>
          <div className="space-y-3">
            <Button onClick={() => { setSuccess(false); setStep(1); setFormData({ service_type: '', name: '', phone: '', vehicle_type: 'sedan', service_id: '', date: null, time_block: '', notes: '', address: '', google_maps_link: '', units: 1, branch_id: '' }); setAdditionalUnits([]); setServiceCategory(''); }} className="w-full" data-testid="new-booking-btn">
              Agendar otra cita
            </Button>
            <Link to="/lealtad">
              <Button variant="outline" className="w-full mt-2">Acceso Clientes</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">WD Lavado</h1>
          </div>
          <p className="text-slate-600">Agenda tu servicio de lavado</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1,2,3,4,5].map(s => (
            <div key={s} className={cn("w-10 h-1.5 rounded-full transition-colors", s <= step ? "bg-blue-600" : "bg-slate-200")} />
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Service Type */}
            {step === 1 && (
              <div className="space-y-6 animate-slideIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Tipo de Servicio</h2>
                  <p className="text-slate-600 mt-1">¿Cómo prefieres tu servicio?</p>
                </div>
                <div className="grid gap-4">
                  {SERVICE_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        className={cn(
                          "p-5 border-2 rounded-xl text-left transition-all flex items-center gap-4",
                          formData.service_type === type.value
                            ? "border-blue-600 bg-blue-50"
                            : "border-slate-200 hover:border-blue-400"
                        )}
                        onClick={() => setFormData({ ...formData, service_type: type.value })}
                        data-testid={`service-type-${type.value}`}
                      >
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                          <Icon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{type.label}</div>
                          <div className="text-sm text-slate-500">{type.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <Button type="button" className="w-full h-14 text-lg" disabled={!formData.service_type} onClick={() => setStep(2)} data-testid="next-step-1-btn">
                  Continuar <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Select Service */}
            {step === 2 && (
              <div className="space-y-6 animate-slideIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Elige tu Servicio</h2>
                  <p className="text-slate-600 mt-1">Selecciona la categoría y servicio</p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center" data-testid="booking-category-filter">
                  {[...new Set(services.map(s => s.category).filter(Boolean))].map(cat => (
                    <button key={cat} type="button" className={cn(
                      "px-4 py-2 text-sm font-medium rounded-full transition-colors",
                      serviceCategory === cat ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    )} onClick={() => setServiceCategory(cat)} data-testid={`booking-category-${cat}`}>{cat}</button>
                  ))}
                </div>
                {!serviceCategory && (
                  <div className="text-center py-8 text-slate-400">
                    <Car className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Selecciona una categoría para ver los servicios</p>
                  </div>
                )}
                {serviceCategory && (() => {
                  const filtered = services.filter(s => s.category === serviceCategory);
                  const grouped = {};
                  filtered.forEach(s => { const sub = s.subcategory || 'General'; if (!grouped[sub]) grouped[sub] = []; grouped[sub].push(s); });
                  return Object.entries(grouped).map(([sub, svcs]) => (
                    <div key={sub}>
                      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{sub}</h3>
                      <div className="grid gap-3">
                        {svcs.map(service => (
                          <button key={service.id} type="button" className={cn(
                            "p-4 border-2 rounded-xl text-left transition-all",
                            formData.service_id === service.id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-400"
                          )} onClick={() => setFormData({ ...formData, service_id: service.id })} data-testid={`service-option-${service.id}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-semibold text-slate-900">{service.name}</div>
                                <div className="text-sm text-slate-500 mt-1"><Clock className="w-3 h-3 inline mr-1" />{service.duration} minutos</div>
                              </div>
                              <div className="text-xl font-bold text-blue-600">{formatCurrency(service.price)}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-14 text-lg">
                    <ArrowLeft className="w-5 h-5 mr-2" /> Atrás
                  </Button>
                  <Button type="button" className="flex-1 h-14 text-lg" disabled={!formData.service_id} onClick={() => setStep(3)} data-testid="next-step-2-btn">
                    Continuar <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Date & Time Block / Branch */}
            {step === 3 && (
              <div className="space-y-6 animate-slideIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">
                    {formData.service_type === 'centro_lavado' ? 'Fecha y Sucursal' : 'Fecha y Horario'}
                  </h2>
                  <p className="text-slate-600 mt-1">
                    {formData.service_type === 'centro_lavado'
                      ? 'Selecciona la fecha y sucursal'
                      : 'Selecciona cuándo prefieres tu servicio'}
                  </p>
                </div>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                    locale={es}
                    className="rounded-md border"
                    data-testid="booking-calendar"
                  />
                </div>
                {formData.date && (
                  <div className="space-y-3">
                    <p className="text-center text-sm text-slate-600 font-medium">
                      {format(formData.date, "EEEE d 'de' MMMM", { locale: es })}
                    </p>

                    {/* Centro de lavado: show schedule label + branch selector */}
                    {formData.service_type === 'centro_lavado' ? (
                      <>
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-center" data-testid="schedule-label">
                          <div className="flex items-center justify-center gap-2 text-blue-800 font-semibold">
                            <Clock className="w-5 h-5" />
                            Horario de 8:30 a 3:30
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Sucursal *</Label>
                          {branches.length === 0 ? (
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg text-center text-sm text-slate-500">
                              No hay sucursales disponibles
                            </div>
                          ) : (
                            <div className="mt-2 grid gap-3">
                              {branches.map(branch => (
                                <button
                                  key={branch.id}
                                  type="button"
                                  className={cn(
                                    "p-4 border-2 rounded-xl text-left transition-all flex items-center gap-3",
                                    formData.branch_id === branch.id
                                      ? "border-blue-600 bg-blue-50"
                                      : "border-slate-200 hover:border-blue-400"
                                  )}
                                  onClick={() => setFormData({ ...formData, branch_id: branch.id })}
                                  data-testid={`branch-option-${branch.id}`}
                                >
                                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                                    <Building2 className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <div className="font-semibold text-slate-900">{branch.name}</div>
                                    {branch.address && <div className="text-sm text-slate-500">{branch.address}</div>}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Domicilio / Recogida: show time blocks */
                      <>
                        {loadingBlocks ? (
                          <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
                        ) : (
                          <div className="grid gap-3">
                            {Object.entries(TIME_BLOCK_LABELS).map(([key, block]) => {
                              const blockData = availableBlocks[key];
                              const isAvailable = blockData?.available !== false;
                              return (
                                <button
                                  key={key} type="button"
                                  className={cn(
                                    "p-4 border-2 rounded-xl text-left transition-all",
                                    !isAvailable ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed" :
                                    formData.time_block === key ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-400"
                                  )}
                                  onClick={() => isAvailable && setFormData({ ...formData, time_block: key })}
                                  disabled={!isAvailable}
                                  data-testid={`time-block-${key}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="text-2xl">{block.icon}</span>
                                      <div>
                                        <div className="font-semibold text-slate-900">{block.label}</div>
                                        <div className="text-sm text-slate-500">{block.time}</div>
                                        {block.note && (
                                          <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                                            <Info className="w-3 h-3 shrink-0" />
                                            {block.note}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {!isAvailable && <span className="text-xs text-slate-400">Lleno</span>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1 h-14 text-lg">
                    <ArrowLeft className="w-5 h-5 mr-2" /> Atrás
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 h-14 text-lg"
                    disabled={!formData.date || (formData.service_type === 'centro_lavado' ? !formData.branch_id : !formData.time_block)}
                    onClick={() => setStep(4)}
                    data-testid="next-step-3-btn"
                  >
                    Continuar <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Personal Info + Address */}
            {step === 4 && (
              <div className="space-y-6 animate-slideIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Tus Datos</h2>
                  <p className="text-slate-600 mt-1">Información de contacto</p>
                </div>
                <div>
                  <Label htmlFor="name">Nombre completo *</Label>
                  <Input id="name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Tu nombre" required className="mt-1" data-testid="booking-name" />
                </div>
                <div>
                  <Label htmlFor="phone">Teléfono *</Label>
                  <Input id="phone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="55 1234 5678" required className="mt-1" data-testid="booking-phone" />
                </div>
                <div>
                  <Label>Tipo de vehículo</Label>
                  <Select value={formData.vehicle_type} onValueChange={v => setFormData({...formData, vehicle_type: v})}>
                    <SelectTrigger className="mt-1" data-testid="booking-vehicle"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VEHICLE_TYPES.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cantidad de unidades</Label>
                  <div className="flex items-center gap-3 mt-1" data-testid="units-selector">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      disabled={formData.units <= 1}
                      onClick={() => {
                        const newUnits = Math.max(1, formData.units - 1);
                        setFormData({...formData, units: newUnits});
                        setAdditionalUnits(prev => prev.slice(0, newUnits - 1));
                      }}
                      data-testid="units-minus-btn"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <div className="flex-1 text-center">
                      <span className="text-2xl font-bold text-slate-900" data-testid="units-count">{formData.units}</span>
                      <span className="text-sm text-slate-500 ml-2">{formData.units === 1 ? 'unidad' : 'unidades'}</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      onClick={() => {
                        const newUnits = formData.units + 1;
                        setFormData({...formData, units: newUnits});
                        setAdditionalUnits(prev => [...prev, { service_id: formData.service_id, vehicle_type: 'sedan' }]);
                      }}
                      data-testid="units-plus-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {/* Additional unit forms */}
                {additionalUnits.map((unit, idx) => (
                  <div key={idx} className="border-2 border-blue-200 bg-blue-50/50 rounded-xl p-4 space-y-3" data-testid={`additional-unit-${idx + 2}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{idx + 2}</span>
                      </div>
                      <span className="font-semibold text-slate-800 text-sm">Unidad {idx + 2}</span>
                    </div>
                    <div>
                      <Label className="text-xs">Servicio</Label>
                      <Select
                        value={unit.service_id}
                        onValueChange={v => {
                          setAdditionalUnits(prev => prev.map((u, i) => i === idx ? {...u, service_id: v} : u));
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid={`unit-${idx + 2}-service-select`}>
                          <SelectValue placeholder="Seleccionar servicio..." />
                        </SelectTrigger>
                        <SelectContent>
                          {services.filter(s => s.active !== false).map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name} - {formatCurrency(s.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Tipo de vehiculo</Label>
                      <Select
                        value={unit.vehicle_type}
                        onValueChange={v => {
                          setAdditionalUnits(prev => prev.map((u, i) => i === idx ? {...u, vehicle_type: v} : u));
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid={`unit-${idx + 2}-vehicle-select`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VEHICLE_TYPES.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {needsAddress && (
                  <>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700 font-medium flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Se requiere dirección o link de Google Maps
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="address">Dirección</Label>
                      <Input id="address" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Calle, número, colonia..." className="mt-1" data-testid="booking-address" />
                    </div>
                    <div>
                      <Label htmlFor="maps_link">Link de Google Maps</Label>
                      <Input id="maps_link" value={formData.google_maps_link} onChange={e => setFormData({...formData, google_maps_link: e.target.value})} placeholder="https://maps.google.com/..." className="mt-1" data-testid="booking-maps-link" />
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="notes">Notas</Label>
                  <Input id="notes" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Instrucciones especiales..." className="mt-1" data-testid="booking-notes" />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(3)} className="flex-1 h-14 text-lg">
                    <ArrowLeft className="w-5 h-5 mr-2" /> Atrás
                  </Button>
                  <Button type="button" className="flex-1 h-14 text-lg" disabled={!formData.name || !formData.phone || (needsAddress && !formData.address && !formData.google_maps_link) || additionalUnits.some(u => !u.service_id)} onClick={() => setStep(5)} data-testid="next-step-4-btn">
                    Continuar <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Confirmation */}
            {step === 5 && (
              <div className="space-y-6 animate-slideIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Confirmar Cita</h2>
                  <p className="text-slate-600 mt-1">Revisa los datos de tu cita</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                  <div className="flex justify-between"><span className="text-slate-500">Tipo</span><span className="font-medium">{SERVICE_TYPES.find(t => t.value === formData.service_type)?.label}</span></div>
                  
                  {/* Unit 1 */}
                  <div className="border-l-4 border-blue-500 pl-3 space-y-1">
                    <div className="text-xs font-semibold text-blue-600 uppercase">Unidad 1</div>
                    <div className="flex justify-between"><span className="text-slate-500 text-sm">Servicio</span><span className="font-medium text-sm">{services.find(s => s.id === formData.service_id)?.name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500 text-sm">Vehiculo</span><span className="font-medium text-sm">{VEHICLE_TYPES.find(v => v.value === formData.vehicle_type)?.label}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500 text-sm">Precio</span><span className="font-bold text-blue-600 text-sm">{formatCurrency(services.find(s => s.id === formData.service_id)?.price || 0)}</span></div>
                  </div>

                  {/* Additional units */}
                  {additionalUnits.map((unit, idx) => {
                    const unitService = services.find(s => s.id === unit.service_id);
                    return (
                      <div key={idx} className="border-l-4 border-blue-500 pl-3 space-y-1" data-testid={`confirm-unit-${idx + 2}`}>
                        <div className="text-xs font-semibold text-blue-600 uppercase">Unidad {idx + 2}</div>
                        <div className="flex justify-between"><span className="text-slate-500 text-sm">Servicio</span><span className="font-medium text-sm">{unitService?.name || 'No seleccionado'}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500 text-sm">Vehiculo</span><span className="font-medium text-sm">{VEHICLE_TYPES.find(v => v.value === unit.vehicle_type)?.label}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500 text-sm">Precio</span><span className="font-bold text-blue-600 text-sm">{formatCurrency(unitService?.price || 0)}</span></div>
                      </div>
                    );
                  })}

                  {/* Delivery fee line when applicable */}
                  {needsAddress && deliveryFee > 0 && (
                    <div
                      className="border-l-4 border-amber-500 pl-3 space-y-1"
                      data-testid="booking-delivery-fee-line"
                    >
                      <div className="text-xs font-semibold text-amber-600 uppercase">Servicio extra</div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 text-sm">
                          {formData.service_type === 'domicilio' ? 'Cargo a domicilio' : 'Cargo por recogida y entrega'}
                        </span>
                        <span className="font-bold text-amber-600 text-sm">{formatCurrency(deliveryFee)}</span>
                      </div>
                    </div>
                  )}

                  {/* Total */}
                  {(() => {
                    const mainPrice = services.find(s => s.id === formData.service_id)?.price || 0;
                    const additionalTotal = additionalUnits.reduce((sum, u) => sum + (services.find(s => s.id === u.service_id)?.price || 0), 0);
                    const surcharge = needsAddress ? Number(deliveryFee || 0) : 0;
                    const total = mainPrice + additionalTotal + surcharge;
                    return (
                      <div className="flex justify-between border-t border-slate-200 pt-2">
                        <span className="text-slate-700 font-semibold">Total estimado</span>
                        <span className="font-bold text-lg text-emerald-600">{formatCurrency(total)}</span>
                      </div>
                    );
                  })()}

                  <div className="flex justify-between"><span className="text-slate-500">Fecha</span><span className="font-medium">{formData.date ? format(formData.date, "d MMM yyyy", { locale: es }) : ''}</span></div>
                  {formData.service_type === 'centro_lavado' ? (
                    <>
                      <div className="flex justify-between"><span className="text-slate-500">Horario</span><span className="font-medium">8:30 a 3:30</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Sucursal</span><span className="font-medium">{branches.find(b => b.id === formData.branch_id)?.name || '—'}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between"><span className="text-slate-500">Horario</span><span className="font-medium">{TIME_BLOCK_LABELS[formData.time_block]?.label} - {TIME_BLOCK_LABELS[formData.time_block]?.time}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">{formData.name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Telefono</span><span className="font-medium">{formData.phone}</span></div>
                  {formData.address && <div className="flex justify-between"><span className="text-slate-500">Direccion</span><span className="font-medium text-right max-w-[200px]">{formData.address}</span></div>}
                </div>
                {needsAddress && (
                  <div
                    className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200"
                    data-testid="extra-charge-notice"
                  >
                    <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <span className="font-semibold">Cargo extra por servicio por confirmar.</span>{' '}
                      El costo adicional por {formData.service_type === 'domicilio' ? 'servicio a domicilio' : 'recogida y entrega'} depende de la distancia
                      y se te notificará por WhatsApp antes de confirmar la cita.
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(4)} className="flex-1 h-14 text-lg">
                    <ArrowLeft className="w-5 h-5 mr-2" /> Atrás
                  </Button>
                  <Button type="submit" className="flex-1 h-14 text-lg" disabled={submitting} data-testid="submit-booking-btn">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Enviar Solicitud <CheckCircle className="w-5 h-5 ml-2" /></>}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/lealtad" className="text-blue-600 hover:underline text-sm">
            ¿Ya eres cliente? Accede a tu portal
          </Link>
        </div>
      </div>
    </div>
  );
}
